import { Link } from "react-router-dom";
import { Minus, Plus, ShoppingBag, X } from "lucide-react";
import { useCart } from "@/hooks/use-cart";
import { formatINR } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ProductImage } from "@/components/product-image";

export function CartDrawer() {
  const { cart, open, setOpen, updateQty, removeItem } = useCart();

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        className="absolute inset-0 cursor-pointer bg-foreground/30"
        aria-label="Close cart"
        onClick={() => setOpen(false)}
      />
      <aside
        className="relative flex h-full w-full max-w-md flex-col bg-background shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-label="Shopping cart"
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div className="flex items-center gap-2">
            <ShoppingBag className="h-4 w-4 text-primary" aria-hidden />
            <h2 className="font-display text-xl font-semibold">Your cart</h2>
            {cart?.itemCount ? (
              <span className="text-sm text-muted">({cart.itemCount})</span>
            ) : null}
          </div>
          <button
            type="button"
            className="cursor-pointer p-2 text-muted hover:text-foreground"
            aria-label="Close"
            onClick={() => setOpen(false)}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {!cart?.items.length ? (
            <p className="text-sm text-muted">Your cart is empty.</p>
          ) : (
            <ul className="space-y-5">
              {cart.items.map((item) => (
                <li key={item.id} className="flex gap-4">
                  <div className="w-20 shrink-0">
                    {item.imageUrl ? (
                      <ProductImage
                        src={item.imageUrl}
                        alt=""
                        frameClassName="aspect-square"
                        imgClassName="p-1.5"
                      />
                    ) : (
                      <div className="aspect-square bg-secondary" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/products/${item.slug}`}
                      className="font-medium hover:text-primary cursor-pointer"
                      onClick={() => setOpen(false)}
                    >
                      {item.name}
                    </Link>
                    <p className="text-sm text-muted">
                      {formatINR(item.unitPrice)}
                      {item.weight ? ` · ${item.weight}` : ""}
                    </p>
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        type="button"
                        className="cursor-pointer rounded border border-border p-1"
                        aria-label="Decrease quantity"
                        onClick={() =>
                          void updateQty(item.id, Math.max(0, item.quantity - 1))
                        }
                      >
                        <Minus className="h-3.5 w-3.5" />
                      </button>
                      <span className="w-6 text-center text-sm">
                        {item.quantity}
                      </span>
                      <button
                        type="button"
                        className="cursor-pointer rounded border border-border p-1"
                        aria-label="Increase quantity"
                        onClick={() => void updateQty(item.id, item.quantity + 1)}
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        className="ml-auto cursor-pointer text-xs text-muted hover:text-destructive"
                        onClick={() => void removeItem(item.id)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                  <p className="text-sm font-medium">
                    {formatINR(item.lineTotal)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border-t border-border px-5 py-5">
          <div className="mb-3 flex justify-between text-sm">
            <span className="text-muted">Subtotal</span>
            <span className="font-medium">
              {formatINR(cart?.subtotal ?? 0)}
            </span>
          </div>
          {cart?.checkout.message ? (
            <p className="mb-4 rounded-md bg-secondary p-3 text-xs leading-relaxed text-foreground">
              {cart.checkout.message}
            </p>
          ) : (
            <p className="mb-4 text-xs text-muted">
              Shipping & GST calculated at checkout.
            </p>
          )}
          <div className="flex flex-col gap-2">
            <Link to="/checkout" onClick={() => setOpen(false)}>
              <Button className="w-full" disabled={!cart?.items.length}>
                Checkout
              </Button>
            </Link>
            <Button variant="outline" className="w-full" onClick={() => setOpen(false)}>
              Continue shopping
            </Button>
          </div>
        </div>
      </aside>
    </div>
  );
}
